import {
  cloneElement, isValidElement, useCallback, useEffect, useMemo, useRef, useState,
  type CSSProperties, type MouseEvent as ReactMouseEvent, type ReactNode,
} from 'react';
import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';
import './invoicing.css';
import { useAuthStore } from '../../../auth/store';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { GridSearch } from '../../../components/GridSearch';
import { useSaveFlow } from '../../../components/confirm';
import { formatCombo, useHotkeys } from '../../../components/hotkeys';
import { useNotify } from '../../../components/notifications';
import { UsDateInput } from '../../../components/UsDateInput';
import { focusFirstFormControl } from '../../../hooks/useAutoFocusFirstControl';
import { guardedExit, useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { fmtLocalDate } from '../../../utils/datetime';
import { ItemChargePickerDialog } from '../quotes/ItemChargePickerDialog';
import { CHARGE_TYPE, type ItemChargeCandidate } from '../quotes/detailTypes';
import { apiErrorMessage, createDefaultPrice, createInvoice, draftInvoice, previewInvoice } from './api';
import type {
  InvoiceChargeRow, InvoiceDraftRequest, InvoiceHeader, InvoiceHeaderInput, InvoiceItemInput,
  InvoiceItemRow, InvoiceLookups, InvoiceNoteInput, InvoiceRowTatChoice, PricedInvoice,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Invoicing ▸ Invoice Queue — the NEW INVOICE detail view.
// Port of legacy ALPACA `Invoicing_DetailView_Queue` (Model.xafml :24864,
// caption "Invoice Queue "), opened by the queue's New for the ticked jobs.
// The list is REPLACED by this page, never a popup (standing rule 12).
//
// FIELDS: every field of the G5 form, in its groups and order (Model.xafml
// layout :24864+). LAYOUT: the approved prototype
// docs/prototypes/Invoice-Queue-New-Invoice-Prototype.html (owner, 2026-10-21):
//   · a summary strip (client · jobs · tests · Amount); Status shows the status
//     Save will set;
//   · fields follow the shared standard ONLY: editable = yellow, read-only =
//     grey (rule 12; Items is the reference), tall fields take two row slots;
//   · Quote ID · Quoted Date · Quoted By on ONE line; Due Date Requested and
//     Completed side by side (Completed amber when it is later); Days Delayed in
//     words; under Priority, the priority the price list implies;
//   · Bill To / Report To collapsed to one line until Edit;
//   · charge rows with no price first; the warning above the grid offers "Set
//     price" per unpriced test (creates the default price through Settings ›
//     Test Pricing and re-prices). The grid itself has G5's columns only
//     (2026-10-26 — the "Price From" / "Constituent Price" columns are gone);
//   · totals pinned above the bottom action bar (the Totals section moved there);
//   · Preview (Alt+P) of the unsaved invoice, rendered with Invoicing_Reports;
//   · ONE save confirmation that recaps the invoice and both legacy warnings.
//
// ⚠️ Owner-approved deviations from the reference form (rule 12), kept to this
// form: the collapsed addresses, the one-line quote, the sticky totals bar
// replacing the Totals section, and the paired due dates. Every G5 field is
// still on the page.
//
// NO PRICE IS COMPUTED HERE. The whole draft comes from
// `POST /invoicing/draft`, called again after every edit, which runs the same
// code as the save (CLAUDE.md §1).
//
// ACTION BAR, top and bottom: Save · Preview · Cancel, right-aligned (the shared
// `.sd-actionbar`). Legacy hides Save and New / Save and Close on this view, and
// Save returns to the Invoice Queue.
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Invoice Queue';
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

type Tab = 'analysis' | 'items' | 'notes';

interface Props {
  /** The ticked jobs (Samplecheckin Oids), all of one client. */
  jobIds: string[];
  onClose: () => void;
  /** The invoice was created; the queue behind should reload. */
  onSaved: () => void;
}

const EMPTY_HEADER: InvoiceHeaderInput = {
  dateInvoiced: null, invoicedById: null,
  paymentMethodId: null, po: null, remark: null, email: null,
  billStreet1: null, billStreet2: null, billCity: null, billState: null, billZipCode: null, billCountry: null,
  sameAddress: false,
  reportStreet1: null, reportStreet2: null, reportCity: null, reportState: null, reportZipCode: null, reportCountry: null,
};

/** The editable header fields, taken from a priced draft. */
function headerInputOf(h: InvoiceHeader): InvoiceHeaderInput {
  return {
    dateInvoiced: h.dateInvoiced, invoicedById: h.invoicedById,
    paymentMethodId: h.paymentMethodId, po: h.po, remark: h.remark, email: h.email,
    billStreet1: h.billStreet1, billStreet2: h.billStreet2, billCity: h.billCity,
    billState: h.billState, billZipCode: h.billZipCode, billCountry: h.billCountry,
    sameAddress: h.sameAddress,
    reportStreet1: h.reportStreet1, reportStreet2: h.reportStreet2, reportCity: h.reportCity,
    reportState: h.reportState, reportZipCode: h.reportZipCode, reportCountry: h.reportCountry,
  };
}

interface NoteRow extends InvoiceNoteInput { date: string; isCaseNarrative?: boolean; attachmentName?: string | null }

/** What a person changed on one charge row (absent = keep the price's own). */
interface RowEdit { discount?: number; description?: string | null; tatId?: string; priceId?: string }

export default function InvoiceDetailPage({ jobIds, onClose, onSaved }: Props) {
  const notify      = useNotify();
  const runSave     = useSaveFlow();
  const meName      = useAuthStore((s) => s.user?.name || s.user?.email || '');
  const pageRef     = useRef<HTMLDivElement>(null);

  const [invoice, setInvoice] = useState<PricedInvoice | null>(null);
  const [lookups, setLookups] = useState<InvoiceLookups>({ priorities: [], paymentMethods: [], employees: [], tats: [] });
  const [header, setHeader]   = useState<InvoiceHeaderInput>(EMPTY_HEADER);
  const [priorityId, setPriorityId] = useState<string | null>(null);
  /** Per-row edits a person made, by row key. */
  const [rowEdits, setRowEdits] = useState<Record<string, RowEdit>>({});
  /** Ticked charge rows (G5 shows the selection column on this grid). */
  const [rowSel, setRowSel]   = useState<Set<string | number>>(new Set());
  /** The row whose TAT / Priority popup or Parameter popup is open. */
  const [tatFor, setTatFor]   = useState<InvoiceChargeRow | null>(null);
  // The "Default Pricing" popup: one ticked price-list row, and its search text.
  const [priceSel, setPriceSel] = useState<Set<string | number>>(new Set());
  const [priceSearch, setPriceSearch] = useState('');
  const [paramsFor, setParamsFor] = useState<InvoiceChargeRow | null>(null);
  /** The right-click "Copy To All Cell" menu (G5 Grid_FillContextMenuItems). */
  const [cellMenu, setCellMenu] = useState<{ x: number; y: number; row: InvoiceChargeRow; field: 'discount' | 'description' } | null>(null);
  /** Final Amounts typed on item rows, by item oid. */
  const [finalOverrides, setFinalOverrides] = useState<Record<string, number>>({});
  const [notes, setNotes]     = useState<NoteRow[]>([]);
  const [noteSel, setNoteSel] = useState<Set<string | number>>(new Set());
  const [noteDraft, setNoteDraft] = useState<{ title: string; text: string } | null>(null);
  const [priceFor, setPriceFor] = useState<{ row: InvoiceChargeRow; priority: string; tat: string; price: string } | null>(null);
  const [addrOpen, setAddrOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty]     = useState(false);
  const [tab, setTab]         = useState<Tab>('analysis');
  const [pickItems, setPickItems] = useState(false);
  const [itemSel, setItemSel] = useState<Set<string | number>>(new Set());

  // Grid edits are not native inputs, so the app-wide tracker cannot see them
  // (standing rule 15).
  useUnsavedGuard(dirty);

  // ── First draft ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await draftInvoice({ jobIds });
        if (cancelled) return;
        setInvoice(res.invoice);
        setLookups(res.lookups);
        setHeader(headerInputOf(res.invoice.header));
        setPriorityId(res.invoice.header.priorityId);
      } catch (e) {
        if (!cancelled) {
          notify.error(apiErrorMessage(e) ?? (e instanceof Error ? e.message : 'Failed to build the invoice.'));
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // The jobs are fixed for the life of this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus the first field once the form shows (standing rule 14).
  useEffect(() => {
    if (loading || !pageRef.current) return;
    const ae = document.activeElement as HTMLElement | null;
    if (ae && (/^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) || ae.isContentEditable)) return;
    focusFirstFormControl(pageRef.current);
  }, [loading]);

  // ── Re-pricing ───────────────────────────────────────────────────────────
  const itemInputs = useCallback((items: InvoiceItemRow[], overrides: Record<string, number>): InvoiceItemInput[] =>
    items.map((i) => ({
      oid: i.oid, sourceOid: i.sourceOid, itemPriceId: i.sourceOid ? null : i.itemPriceId,
      qty: i.qty, discount: i.discount, finalAmountOverride: overrides[i.oid] ?? null, remark: i.remark,
    })), []);

  const request = useCallback((over: Partial<{
    priorityId: string | null;
    rowEdits: Record<string, RowEdit>;
    items: InvoiceItemInput[];
  }> = {}): InvoiceDraftRequest => {
    const edits = over.rowEdits ?? rowEdits;
    return {
      jobIds,
      priorityId: over.priorityId !== undefined ? over.priorityId : priorityId,
      // Omitted parts are kept by the server (a TAT pick without a discount
      // keeps the new price's own discount).
      rows: Object.entries(edits).map(([key, e]) => ({
        key, discount: e.discount ?? null, description: e.description,
        tatId: e.tatId ?? null, priceId: e.priceId ?? null,
      })),
      items: over.items ?? (invoice ? itemInputs(invoice.items, finalOverrides) : undefined),
      header,
      notes: notes.map(({ oid, title, text }) => ({ oid, title, text })),
    };
  }, [jobIds, priorityId, rowEdits, invoice, finalOverrides, header, notes, itemInputs]);

  /** Requests are numbered so a slow answer can never overwrite a newer one. */
  const calcSeq = useRef(0);

  async function reprice(next: InvoiceDraftRequest): Promise<boolean> {
    const seq = ++calcSeq.current;
    try {
      const res = await draftInvoice(next, true);
      if (seq !== calcSeq.current) return true;          // superseded
      setInvoice(res.invoice);
      setDirty(true);
      return true;
    } catch (e) {
      notify.warning(apiErrorMessage(e) ?? (e instanceof Error ? e.message : 'Could not price the invoice.'));
      return false;
    }
  }

  function changePriority(id: string | null) {
    // The header Priority re-prices EVERY row (legacy :1883-2014), so a TAT
    // picked earlier for a single row is dropped.
    const next = Object.fromEntries(Object.entries(rowEdits).map(([k, e]) => [k, { ...e, tatId: undefined, priceId: undefined }]));
    setPriorityId(id);
    setRowEdits(next);
    void reprice(request({ priorityId: id, rowEdits: next }));
  }

  /** Open the row's "Default Pricing" popup with nothing ticked (legacy). */
  function openTatPopup(row: InvoiceChargeRow) {
    setPriceSel(new Set());
    setPriceSearch('');
    setTatFor(row);
  }

  /**
   * "Default Pricing" OK (legacy TAT_dc_Accepting :7134): exactly one price-list
   * row must be ticked ("Select atleast one checkbox."). Its priority's price is
   * applied at the row's CURRENT turnaround when that price lists it, else at the
   * shortest turnaround it lists. (Legacy looks the whole ";"-joined TAT list up
   * as ONE TAT name, so for any price listing several TATs it ignores the pick
   * and re-prices with whatever price SQL returns first — not reproduced.)
   */
  function acceptPricePopup() {
    if (!tatFor) return;
    const picked = [...priceSel].map(String);
    if (picked.length === 0) { notify.warning('Select atleast one checkbox.'); return; }
    if (picked.length > 1) { notify.warning('Select only one price.'); return; }
    const choices = tatFor.tatChoices.filter((c) => c.priceId === picked[0]);
    const choice = choices.find((c) => c.tatId === tatFor.tatId) ?? choices[0];
    if (choice) pickTat(tatFor, choice);
  }

  /** Re-price this row at the picked price and turnaround (legacy
   *  TATValuesChanged :6434-6535; the discount resets to the new price's). */
  function pickTat(row: InvoiceChargeRow, choice: InvoiceRowTatChoice) {
    setTatFor(null);
    const next = { ...rowEdits, [row.key]: { ...rowEdits[row.key], tatId: choice.tatId, priceId: choice.priceId, discount: undefined } };
    setRowEdits(next);
    void reprice(request({ rowEdits: next }));
  }

  /** Right-click → "Copy To All Cell": the clicked cell's value onto every row
   *  (legacy Grid_FillContextMenuItems "CopyToAllCell"). */
  function copyToAll() {
    if (!cellMenu || !invoice) return;
    const { row, field } = cellMenu;
    setCellMenu(null);
    const next = { ...rowEdits };
    for (const r of invoice.rows)
      next[r.key] = field === 'discount'
        ? { ...next[r.key], discount: row.discount }
        : { ...next[r.key], description: row.description ?? null };
    setRowEdits(next);
    void reprice(request({ rowEdits: next }));
  }

  function decimalNumber(text: string): number | null {
    const n = Number(text.replace(/[$,%]/g, ''));
    return text.trim() !== '' && Number.isFinite(n) ? n : null;
  }

  function commitRowCell(row: InvoiceChargeRow, column: AppColumn<InvoiceChargeRow>, text: string) {
    if (column.field === 'description') {
      const next = { ...rowEdits, [row.key]: { ...rowEdits[row.key], description: text.trim() || null } };
      setRowEdits(next);
      void reprice(request({ rowEdits: next }));
      return;
    }
    if (column.field !== 'discount') return;
    const discount = decimalNumber(text);
    if (discount === null) { notify.warning('Discount must be a number.'); return; }
    // Mirrors the server rule: refused here so the grid never shows a negative amount.
    if (discount > 100) { notify.warning('Discount cannot be more than 100%.'); return; }
    const next = { ...rowEdits, [row.key]: { ...rowEdits[row.key], discount } };
    setRowEdits(next);
    void reprice(request({ rowEdits: next }));
  }

  /** A TAT or Priority cell — a link to the row's popup when its test has priced turnarounds. */
  function tatCell(r: InvoiceChargeRow, text: string | null) {
    return r.tatChoices.length > 0
      ? <button type="button" className="inv-celllink" onClick={() => openTatPopup(r)}
                title="Change this row's turnaround and priority">{text || '—'}</button>
      : (text ?? '');
  }

  /** Right-click on an editable cell opens "Copy To All Cell". */
  const openCellMenu = (field: 'discount' | 'description') =>
    (e: ReactMouseEvent<HTMLTableCellElement>, r: InvoiceChargeRow) => {
      e.preventDefault();
      setCellMenu({ x: e.clientX, y: e.clientY, row: r, field });
    };

  /** G5 lets Qty and Final Amount be edited; the discount follows from them. */
  function commitItemCell(item: InvoiceItemRow, column: AppColumn<InvoiceItemRow>, text: string) {
    if (!invoice) return;
    let list = invoice.items;
    let overrides = finalOverrides;
    switch (column.field) {
      case 'qty': {
        const qty = Number(text);
        if (!Number.isInteger(qty) || qty <= 0) { notify.warning('Qty value must be greater than 0.'); return; }
        list = list.map((i) => (i.oid === item.oid ? { ...i, qty } : i));
        overrides = Object.fromEntries(Object.entries(overrides).filter(([oid]) => oid !== item.oid));
        break;
      }
      case 'finalAmount': {
        const final = decimalNumber(text);
        if (final === null) { notify.warning('Final Amount must be a number.'); return; }
        overrides = { ...overrides, [item.oid]: final };
        break;
      }
      default: return;
    }
    setFinalOverrides(overrides);
    void reprice(request({ items: itemInputs(list, overrides) }));
  }

  function addItemCharges(picked: ItemChargeCandidate[]) {
    setPickItems(false);
    if (!invoice || picked.length === 0) return;
    const added: InvoiceItemInput[] = picked.map((c) => ({
      oid: crypto.randomUUID(), itemPriceId: c.itemPriceId, qty: 1, discount: 0,
    }));
    void reprice(request({ items: [...itemInputs(invoice.items, finalOverrides), ...added] }));
  }

  function removeItems() {
    if (!invoice || itemSel.size === 0) return;
    const kept = invoice.items.filter((i) => !itemSel.has(i.oid));
    const overrides = Object.fromEntries(Object.entries(finalOverrides).filter(([oid]) => kept.some((i) => i.oid === oid)));
    setItemSel(new Set());
    setFinalOverrides(overrides);
    void reprice(request({ items: itemInputs(kept, overrides) }));
  }

  // ── Notes (legacy Invoicing.Notes — saved with the invoice) ──────────────
  function addNote() {
    if (!noteDraft) return;
    const text = noteDraft.text.trim();
    const title = noteDraft.title.trim() || null;
    // Mirrors InvoiceRules.ValidateNotes, which the save checks again.
    if (!text) { notify.warning('Enter the note text.'); return; }
    if (notes.some((n) => (n.title ?? '') === (title ?? '') && n.text === text)) {
      notify.warning('A note with the same title already exists for this Source ID');
      return;
    }
    setNotes([...notes, { oid: crypto.randomUUID(), title, text, date: new Date().toISOString() }]);
    setNoteDraft(null);
    setDirty(true);
  }

  function removeNotes() {
    setNotes(notes.filter((n) => !noteSel.has(n.oid)));
    setNoteSel(new Set());
    setDirty(true);
  }

  // ── Set price — the missing default price, without leaving the invoice ──
  function openSetPrice(row: InvoiceChargeRow) {
    const regular = lookups.priorities.find((p) => p.isRegular) ?? lookups.priorities[0];
    setPriceFor({ row, priority: regular?.oid ?? '', tat: row.tat ?? lookups.tats[0]?.name ?? '', price: '' });
  }

  async function saveSetPrice() {
    if (!priceFor) return;
    const price = decimalNumber(priceFor.price);
    if (price === null || price < 0) { notify.warning('Enter a price of 0 or more.'); return; }
    const { row } = priceFor;
    if (!row.matrixId || !row.testId || !priceFor.priority || !priceFor.tat) {
      notify.warning('Choose a priority and a TAT.');
      return;
    }
    try {
      await createDefaultPrice({
        matrix: row.matrixId, test: row.testId, isGroup: row.isGroup, component: row.componentId,
        tat: priceFor.tat, priority: priceFor.priority, surchargePrice: price,
      });
      setPriceFor(null);
      notify.success(`Default price saved for ${row.test ?? 'the test'}.`);
      void reprice(request());
    } catch (e) {
      notify.error(apiErrorMessage(e) ?? (e instanceof Error ? e.message : 'The price could not be saved.'));
    }
  }

  function patch(p: Partial<InvoiceHeaderInput>) {
    setHeader((cur) => ({ ...cur, ...p }));
    setDirty(true);
  }

  // ── Preview / Save ───────────────────────────────────────────────────────
  async function preview() {
    if (!invoice) return;
    try {
      const opened = await previewInvoice(request());
      if (!opened) notify.warning('The preview could not be opened — allow pop-ups for this site.');
    } catch (e) {
      notify.error(apiErrorMessage(e) ?? (e instanceof Error ? e.message : 'The preview could not be generated.'));
    }
  }

  /** ONE confirmation: what is about to be saved, and legacy's two warnings. */
  function saveRecap(inv: PricedInvoice): string {
    const lines = [
      `${jobIds.length} job${jobIds.length === 1 ? '' : 's'} · ${inv.rows.length} test row${inv.rows.length === 1 ? '' : 's'}`
        + ` · ${inv.items.length} item charge${inv.items.length === 1 ? '' : 's'} · Amount ${money.format(inv.totals.amount)}.`,
    ];
    if (inv.unpricedTests.length > 0)
      lines.push(`${inv.unpricedTests.join(', ')} Test not set DefaultPrice! They are charged at zero.`);
    if (inv.reportedAfterDueDate)
      lines.push('Reported date exceeds request due date!');
    // Where it goes next — the status the save stores (no Status field on the page).
    if (inv.statusAfterSave) {
      const next = /review/i.test(inv.statusAfterSave) ? 'Invoice Validation' : 'Invoice Delivery';
      lines.push(`It will go to ${next} (${inv.statusAfterSave}).`);
    }
    lines.push('Do you want to Save?');
    return lines.join(' ');
  }

  async function save() {
    if (!invoice) return;
    if (invoice.rows.length === 0 && invoice.items.length === 0) {
      notify.error('There is nothing to invoice for the selected jobs.');
      return;
    }
    // The default "Saved successfully." — a success message does not echo the
    // new id (§18.6); the invoice moves on to Invoice Validation.
    let pdfStored = true;
    await runSave({
      formName: FORM_NAME,
      auditAction: 'create',
      confirmTitle: 'Save invoice?',
      confirmMessage: saveRecap(invoice),
      save: async () => { pdfStored = (await createInvoice(request())).pdfStored; },
      onSaved: () => {
        setDirty(false);
        // The invoice is saved either way; only its stored PDF is missing.
        if (!pdfStored) notify.warning('The invoice was saved, but its PDF could not be generated.');
        onSaved();
      },
      onError: (e) => {
        const message = apiErrorMessage(e);
        if (message) notify.error(message);
      },
    });
  }

  // Keyboard: declared next to the buttons they mirror (rule 14).
  useHotkeys([
    { combo: 'mod+s', label: 'Save',    group: 'New Invoice', run: () => void save(), disabled: loading, order: 1 },
    { combo: 'alt+p', label: 'Preview', group: 'New Invoice', run: () => void preview(), disabled: loading, order: 2 },
    { combo: 'esc',   label: 'Cancel',  group: 'New Invoice', run: () => void guardedExit(() => onClose()), order: 3 },
  ]);

  // ── Grid columns ─────────────────────────────────────────────────────────
  // G5 InvoicingAnalysisCharge_ListView_Queue (Model.xafml :26672), its columns
  // in its order: Qty · PriceCode · Matrix · Test · Method · Description ·
  // Component · Parameter · ChargeType · TAT · Priority · UnitPrice · From · To ·
  // TierNo · Price · Discount% · Amount. Selection, Qty, PriceCode, Matrix and
  // Test are frozen, as G5 freezes them (:3704-3729). Editable as in G5:
  // Description, Discount%. (Surcharge is in G5's model but not shown on its
  // screen, so it is not a column here; Price From / Constituent Price were G6
  // additions, removed 2026-10-26 to match G5.)
  const rowColumns: AppColumn<InvoiceChargeRow>[] = useMemo(() => [
    { field: 'qty',         header: 'Qty',         width: 60, align: 'right', type: 'number' },
    { field: 'priceCode',   header: 'PriceCode',   width: 100 },
    { field: 'matrix',      header: 'Matrix',      width: 90 },
    { field: 'test',        header: 'Test',        width: 170, wrap: true },
    { field: 'method',      header: 'Method',      width: 120 },
    { field: 'description', header: 'Description', width: 160, edit: { type: 'text' },
      onCellContextMenu: openCellMenu('description') },
    { field: 'component',   header: 'Component',   width: 100 },
    // G5: clicking Parameter lists the analytes behind the row (:5969-6025).
    { field: 'parameter',   header: 'Parameter',   width: 90, render: (r) => (
      r.parameters.length > 0
        ? <button type="button" className="inv-celllink" onClick={() => setParamsFor(r)}
                  title={`Show the ${r.parameters.length} parameter${r.parameters.length === 1 ? '' : 's'}`}>
            {r.parameter || 'Parameters'}
          </button>
        : (r.parameter ?? '')
    ) },
    { field: 'chargeType',  header: 'ChargeType',  width: 90, render: (r) => CHARGE_TYPE[r.chargeType] ?? '' },
    // G5: clicking TAT or Priority opens the row's TAT / Priority popup (:6027-6150).
    { field: 'tat',         header: 'TAT',         width: 80, render: (r) => tatCell(r, r.tat) },
    { field: 'priority',    header: 'Priority',    width: 90, render: (r) => tatCell(r, r.priority) },
    { field: 'unitPrice',   header: 'UnitPrice',   width: 95, align: 'right', type: 'number',
      render: (r) => (r.priceSource === 'None'
        ? <span className="inv-muted" title="No price on the quote or the default price list">{money.format(0)}</span>
        : money.format(r.unitPrice)),
      summary: (rs) => money.format(rs.reduce((n, r) => n + r.unitPrice, 0)) },
    { field: 'from',        header: 'From',        width: 60, align: 'right', type: 'number', render: (r) => String(r.from ?? 0) },
    { field: 'to',          header: 'To',          width: 60, align: 'right', type: 'number', render: (r) => String(r.to ?? 0) },
    { field: 'tierNo',      header: 'TierNo',      width: 65, align: 'right', type: 'number', render: (r) => String(r.tierNo ?? 0) },
    { field: 'tierPrice',   header: 'Price',       width: 100, align: 'right', type: 'number',
      summary: (rs) => money.format(rs.reduce((n, r) => n + r.tierPrice, 0)), render: (r) => money.format(r.tierPrice) },
    { field: 'discount',    header: 'Discount%',   width: 90, align: 'right', type: 'number', edit: { type: 'number' },
      render: (r) => r.discount.toFixed(2), onCellContextMenu: openCellMenu('discount') },
    { field: 'amount',      header: 'Amount',      width: 105, align: 'right', type: 'number', render: (r) => money.format(r.amount),
      summary: (rs) => money.format(rs.reduce((n, r) => n + r.amount, 0)) },
    // tatCell / openCellMenu read only stable state setters and lookups.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [lookups]);

  // G5 Invoicing_ItemCharges_ListView, in its order. Editable: Qty, FinalAmount.
  const itemColumns: AppColumn<InvoiceItemRow>[] = useMemo(() => [
    { field: 'itemCode',    header: 'ItemCode',    width: 110 },
    { field: 'qty',         header: 'Qty',         width: 65, align: 'right', type: 'number', edit: { type: 'number' } },
    { field: 'itemName',    header: 'Item',        width: 170 },
    { field: 'description', header: 'Description', width: 190 },
    { field: 'category',    header: 'Category',    width: 120 },
    { field: 'unit',        header: 'Unit',        width: 80 },
    // G5 binds this column to UnitPrice (the price after the item's discount).
    { field: 'unitPrice',   header: 'UnitPrice',   width: 100, align: 'right', type: 'number', render: (r) => money.format(r.unitPrice) },
    { field: 'amount',      header: 'Amount',      width: 100, align: 'right', type: 'number', render: (r) => money.format(r.amount) },
    { field: 'discount',    header: 'Discount',    width: 90, align: 'right', type: 'number', render: (r) => r.discount.toFixed(2) },
    { field: 'finalAmount', header: 'FinalAmount', width: 110, align: 'right', type: 'number', edit: { type: 'number' }, render: (r) => money.format(r.finalAmount) },
  ], []);

  // G5 TestPriceSurcharge_ListView_Invoice ("Default Pricing"): one row per
  // price-list row of the test — its TAT list and its Priority.
  type PriceRow = { priceId: string; tats: string; priority: string | null; current: boolean };
  const priceColumns: AppColumn<PriceRow>[] = useMemo(() => [
    { field: 'tats',     header: 'TAT',      width: 340, wrap: true },
    { field: 'priority', header: 'Priority', width: 160 },
  ], []);
  const priceRows = useMemo<PriceRow[]>(() => {
    if (!tatFor) return [];
    const byPrice = new Map<string, PriceRow>();
    for (const c of tatFor.tatChoices) {
      const r = byPrice.get(c.priceId)
        ?? { priceId: c.priceId, tats: '', priority: c.priority, current: false };
      r.tats = r.tats ? `${r.tats}; ${c.tat ?? ''}` : (c.tat ?? '');
      if (c.priorityId === tatFor.priorityId && c.tatId === tatFor.tatId) r.current = true;
      byPrice.set(c.priceId, r);
    }
    const q = priceSearch.trim().toLowerCase();
    return [...byPrice.values()].filter((r) => !q
      || r.tats.toLowerCase().includes(q) || (r.priority ?? '').toLowerCase().includes(q));
  }, [tatFor, priceSearch]);

  // G5 Invoicing_Notes_ListView (Notes BO list members, in order). A note
  // typed here has no file and is not a case narrative.
  const noteColumns: AppColumn<NoteRow>[] = useMemo(() => [
    { field: 'title',  header: 'Title',  width: 180 },
    { field: 'attachmentName', header: 'Attachment', width: 160 },
    { field: 'text',   header: 'Note' },
    { field: 'author', header: 'Author', width: 160, render: () => meName },
    { field: 'date',   header: 'Date',   width: 150, type: 'datetime' },
    { field: 'isCaseNarrative', header: 'IsCaseNarrative', width: 120, align: 'center',
      render: (r) => <input type="checkbox" checked={!!r.isCaseNarrative} readOnly disabled aria-label="IsCaseNarrative" /> },
  ], [meName]);

  // ── Field helpers — the SHARED detail-view markup (rule 12) ───────────────
  /** A field row, styled ONLY by the shared standard (rule 12): an editable
   *  control is yellow, a read-only one grey — the one cue. `tall` gives a
   *  two-line field two row slots (`sd-row--span2`), so the other column keeps
   *  its 26px rhythm instead of opening a gap. */
  function field(label: string, control: ReactNode, opts: { required?: boolean; tall?: boolean } = {}) {
    return (
      <div className={`sd-row${opts.tall ? ' sd-row--span2 inv-top' : ''}`} key={label}>
        <label className="sd-label">{label}{opts.required && <span className="req">*</span>}</label>
        {isValidElement<{ 'aria-label'?: string }>(control) && typeof control.type === 'string'
          ? cloneElement(control, { 'aria-label': label })
          : control}
      </div>
    );
  }

  function section(title: string, left: ReactNode[], right: ReactNode[], after?: ReactNode) {
    const l = left.filter(Boolean);
    const r = right.filter(Boolean);
    // Row SLOTS, not fields: a tall field fills two.
    const slots = (list: ReactNode[]) => list.reduce<number>((n, f) =>
      n + (isValidElement<{ className?: string }>(f) && f.props.className?.includes('sd-row--span2') ? 2 : 1), 0);
    const rowCount = Math.max(slots(l), slots(r));
    const padOf = (n: number, side: string) => Array.from({ length: rowCount - n },
      (_, i) => <div className="sd-row" aria-hidden="true" key={`pad-${side}-${i}`} />);
    return (
      <>
        <h3 className="sd-section-h">{title}</h3>
        <div className="sd-grid sd-grid--colmajor sd-grid--side" style={{ '--sd-rows': String(rowCount) } as CSSProperties}>
          {l}{padOf(slots(l), 'l')}{r}{padOf(slots(r), 'r')}
        </div>
        {after}
      </>
    );
  }

  const ro = (value: string | null | undefined, extra = '') =>
    <input type="text" className={`sd-input readonly${extra}`} readOnly value={value ?? ''} />;

  function text(key: keyof InvoiceHeaderInput) {
    return (
      // autoComplete off: a client's address is not the user's to save.
      <input type="text" className="sd-input" maxLength={100} autoComplete="off"
             value={(header[key] as string | null) ?? ''}
             onChange={(e) => patch({ [key]: e.target.value || null } as Partial<InvoiceHeaderInput>)} />
    );
  }

  /** While "Same Address" is ticked, Report To mirrors Bill To. */
  const report = (billKey: keyof InvoiceHeaderInput, reportKey: keyof InvoiceHeaderInput) =>
    header.sameAddress ? ro(header[billKey] as string | null) : text(reportKey);

  const tip = (label: string, combo: string) => `${label} (${formatCombo(combo)})`;
  const actionBar = (bottom: boolean) => (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      <button type="button" className="sd-abtn primary" title={tip('Save', 'mod+s')}
              onClick={() => void save()} disabled={loading || !invoice}>
        <span className="tb-i">💾</span> Save
      </button>
      <button type="button" className="sd-abtn" title={tip('Preview the invoice', 'alt+p')}
              onClick={() => void preview()} disabled={loading || !invoice}>
        <i className="ti ti-file-type-pdf inv-pdf" aria-hidden="true" /> Preview
      </button>
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button type="button" className="sd-abtn cancel" title={tip('Cancel', 'esc')} onClick={onClose}>
        <span className="tb-i">↩︎</span> Cancel
      </button>
    </div>
  );

  const h = invoice?.header;
  // Rows with no price first — they are the ones that need attention.
  const rows = useMemo(() => [...(invoice?.rows ?? [])]
    .sort((a, b) => Number(a.priceSource !== 'None') - Number(b.priceSource !== 'None')), [invoice]);
  const items = invoice?.items ?? [];
  const unpriced = invoice?.unpricedTests ?? [];
  const totals = invoice?.totals;
  const reportedLate = !!invoice?.reportedAfterDueDate;
  const billLine = [header.billStreet1, header.billStreet2, header.billCity,
    [header.billState, header.billZipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  const tabs: Array<{ id: Tab; label: string; count: number }> = [
    { id: 'analysis', label: 'Analysis Charge', count: rows.length },
    { id: 'items',    label: 'Item Charge',     count: items.length },
    { id: 'notes',    label: 'Notes',           count: notes.length },
  ];
  const existingItemIds = useMemo(
    () => new Set(items.map((i) => (i.itemPriceId ?? '').toLowerCase())), [items]);

  return (
    <div className="bl-page mlv-host inv-detail">
      <div className="bl-grid-area">
        <div className="sd-wrap" ref={pageRef}>
          <div className="sd-head">
            <div>
              {/* G5's caption was "Invoice Queue – Autogenerate" (the InvoiceID
                  NullText). That box is gone, so the page says what it is. */}
              <h2>New Invoice</h2>
              <div className="sd-bc">
                <span>Accounting</span>
                <i className="ti ti-chevron-right" />
                <span>Invoicing</span>
                <i className="ti ti-chevron-right" />
                <a onClick={onClose}>Invoice Queue</a>
                <i className="ti ti-chevron-right" />
                <span>New Invoice</span>
              </div>
            </div>
          </div>

          {actionBar(false)}

          <div className="sd-body">
            {/* Summary strip — what is being billed, at a glance. */}
            <div className="inv-strip">
              <div className="inv-tile"><div className="inv-k">Client</div><div className="inv-v">{h?.clientName ?? ''}</div></div>
              <div className="inv-tile">
                <div className="inv-k">Jobs</div>
                <div>{(h?.jobIdText ?? '').split(';').map((j) => j.trim()).filter(Boolean)
                  .map((j) => <span className="inv-chip" key={j}>{j}</span>)}</div>
              </div>
              <div className="inv-tile">
                <div className="inv-k">Tests</div>
                <div className="inv-v">{rows.length}</div>
              </div>
              <div className="inv-tile"><div className="inv-k">Amount</div><div className="inv-big">{money.format(totals?.amount ?? 0)}</div></div>
            </div>

            {/* Invoice ID (always "Autogenerate" here — Save returns to the queue),
                Client and Job ID are left out: the strip above already shows them. */}
            {/* Balanced 8 | 7 (owner 2026-10-26): billing on the left, schedule and
                pricing on the right — the same split as Invoice Validation. */}
            {section('Summary', [
              field('Invoiced Date',
                // The shared MM/DD/YYYY field — a native date input follows the
                // browser's region and showed 21/10/2026 (standing rule 3).
                <UsDateInput className="sd-input" value={dateInput(header.dateInvoiced) || today()}
                             onChange={(iso) => patch({ dateInvoiced: iso ? `${iso}T00:00:00` : null })} />),
              field('Invoiced By',
                <select className="sd-input" value={header.invoicedById ?? ''} title="Alt+↓ to open"
                        onChange={(e) => patch({ invoicedById: e.target.value || null })}>
                  <option value="" />
                  {lookups.employees.map((p) => <option key={p.id} value={p.id}>{p.name ?? ''}</option>)}
                </select>),
              field('Project ID', ro(h?.projectCode)),
              field('Project Name', ro(h?.projectName)),
              field('Payment Method',
                <select className="sd-input" value={header.paymentMethodId ?? ''} title="Alt+↓ to open"
                        onChange={(e) => patch({ paymentMethodId: e.target.value || null })}>
                  <option value="" />
                  {lookups.paymentMethods.map((p) => <option key={p.id} value={p.id}>{p.name ?? ''}</option>)}
                </select>),
              // Next to Payment Method — both are billing details.
              field('PO #', text('po')),
              field('Remark',
                <textarea rows={2} className="sd-input sd-input--tall" value={header.remark ?? ''}
                          onChange={(e) => patch({ remark: e.target.value || null })} />,
                { tall: true }),
            ], [
              // Quote ID · Quoted Date · Quoted By, on one line.
              field('Quote',
                <div className="inv-robox" title="Quote ID · Quoted Date · Quoted By">
                  {h?.quoteCode
                    ? <><b>{h.quoteCode}</b><span className="inv-dot">·</span>{fmtLocalDate(h.quotedDate ?? null)}
                        <span className="inv-dot">·</span>{h.quotedByName ?? ''}</>
                    : <span className="inv-muted">No quote on these jobs</span>}
                </div>),
              // Due Date Requested / Completed side by side — Completed amber when
              // it is later, the condition legacy only warns about on Save.
              field('Due Date',
                <div className="inv-pair">
                  <label><small>Requested</small>{ro(fmtLocalDate(h?.dueDate ?? null))}</label>
                  <label><small>Completed</small>{ro(fmtLocalDate(h?.dueDateCompleted ?? null), reportedLate ? ' inv-late-box' : '')}</label>
                </div>, { tall: true }),
              field('TAT', ro(h?.tatName)),
              field('Priority',
                <select className="sd-input" value={priorityId ?? ''} disabled={loading}
                        title="Changing it re-prices the rows from the price list (Alt+↓ to open)"
                        onChange={(e) => changePriority(e.target.value || null)}>
                  <option value="" />
                  {lookups.priorities.map((p) => <option key={p.oid} value={p.oid}>{p.name ?? ''}</option>)}
                </select>),
              // As G5 counts it (Invoicing.cs :302-313): from the requested due
              // date to TODAY, not to the completed date.
              field('Days Delayed',
                <div className="inv-robox" title="Days from the requested due date to today, as ALPACA counts it">
                  {h && h.daysDelayed > 0
                    ? <span className="inv-pill inv-pill--late">{h.daysDelayed} days late</span>
                    : <span className="inv-pill inv-pill--ok">On time</span>}
                </div>),
              // The priority the price list implies for the jobs' turnaround (G5
              // ActualPriorityApplied, stored on save), read-only.
              field('Actual Priority Applied',
                ro(lookups.priorities.find((p) => p.oid === h?.actualPriorityId)?.name)),
            ])}

            {/* Contacts — read-only, from the bill-to contact, in the same two
                columns as Summary (G5 layout); empty boxes when the jobs carry no contact. */}
            <div className="inv-contacts">
              {section('Contacts', [
                field('Primary Contact', ro(h?.primaryContactName)),
                field('Contact Email', ro(h?.contactEmail)),
              ], [
                field('Contact Phone', ro(h?.contactPhone)),
              ])}
            </div>

            {/* Bill To / Report To — a standard section heading (aligned with
                Summary / Contacts) with one summary line until Edit. */}
            <h3 className="sd-section-h inv-addr-h">
              Addresses
              <button type="button" className="inv-addredit" onClick={() => setAddrOpen(!addrOpen)}
                      aria-expanded={addrOpen}>
                {addrOpen ? 'Collapse' : 'Edit'}
              </button>
            </h3>
            {!addrOpen && (
              <div className="inv-muted inv-addrprev">
                Bill to: {billLine || '—'} · Report to: {header.sameAddress ? 'same as bill to' : 'its own address'}
              </div>
            )}
            {addrOpen && (
              <div>
                {section('Bill To Address', [
                  field('Street 1', text('billStreet1')),
                  field('City', text('billCity')),
                  field('Country', text('billCountry')),
                ], [
                  field('Street 2', text('billStreet2')),
                  field('State', text('billState')),
                  field('Zip', text('billZipCode')),
                ],
                  // In a side-label grid of its own, so the box lines up with the
                  // field column above it (the shared checkbox-row pattern).
                  <div className="sd-grid sd-grid--side inv-same">
                    <div className="sd-row sd-row--check">
                      <span className="sd-label" aria-hidden="true">&nbsp;</span>
                      <label className="sd-check">
                        <input type="checkbox" checked={header.sameAddress}
                               onChange={(e) => patch({ sameAddress: e.target.checked })} />
                        Same Address For Both Shipping And Billing
                      </label>
                    </div>
                  </div>,
                )}
                {section('Report To Address', [
                  field('Street 1', report('billStreet1', 'reportStreet1')),
                  field('City', report('billCity', 'reportCity')),
                  field('Country', report('billCountry', 'reportCountry')),
                ], [
                  field('Street 2', report('billStreet2', 'reportStreet2')),
                  field('State', report('billState', 'reportState')),
                  field('Zip', report('billZipCode', 'reportZipCode')),
                ])}
              </div>
            )}

            {unpriced.length > 0 && (
              <div className="inv-note" role="status">
                <i className="ti ti-alert-triangle" aria-hidden="true" />
                <span>
                  <b>{unpriced.join(', ')}</b> {unpriced.length === 1 ? 'has' : 'have'} no price on the quote or the
                  default price list, so {unpriced.length === 1 ? 'it is' : 'they are'} charged at zero.{' '}
                  {rows.filter((r) => r.priceSource === 'None').map((r) => (
                    <button key={r.key} type="button" className="inv-setprice" onClick={() => openSetPrice(r)}
                            title={`Set a default price for ${r.test ?? 'this test'}`}>
                      Set price: {r.test ?? ''}
                    </button>
                  ))}{' '}
                  <a href="/m/set-test-default-pricing" target="_blank" rel="noreferrer">
                    Set prices in Test Pricing <i className="ti ti-external-link" aria-hidden="true" />
                  </a>
                </span>
              </div>
            )}

            <div className="sd-assoc-tabbar sd-childtabbar" role="tablist">
              {tabs.map((t) => (
                <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
                        className={`sd-assoc-tab${tab === t.id ? ' active' : ''}`}
                        onClick={() => setTab(t.id)}>
                  {t.label}<span className="sd-assoc-tabn">{t.count}</span>
                </button>
              ))}
            </div>

            <div role="tabpanel" className="sd-childtabpanel inv-tabpanel" hidden={tab !== 'analysis'}>
              <AppDataGrid<InvoiceChargeRow>
                rows={rows}
                columns={rowColumns}
                getRowId={(r) => r.key}
                loading={loading}
                // G5: selection, Qty, PriceCode, Matrix and Test frozen on the left (:3704-3729).
                selectable
                selectedIds={rowSel}
                onSelectionChange={setRowSel}
                stickyColumns={4}
                onCellCommit={commitRowCell}
                maximizable
                exportFilename="new-invoice-analysis-charge"
                emptyText="No tests left to invoice on these jobs."
              />
            </div>

            <div role="tabpanel" className="sd-childtabpanel inv-tabpanel" hidden={tab !== 'items'}>
              <div className="inv-tabbar">
                <button type="button" className="ca-btn" onClick={() => setPickItems(true)} disabled={loading}>
                  <i className="ti ti-plus tb-i-green" aria-hidden="true" /> Add
                </button>
                <button type="button" className="ca-btn" onClick={removeItems} disabled={itemSel.size === 0}>
                  <i className="ti ti-minus tb-i-red" aria-hidden="true" /> Remove
                </button>
              </div>
              <AppDataGrid<InvoiceItemRow>
                rows={items}
                columns={itemColumns}
                getRowId={(r) => r.oid}
                loading={loading}
                selectable
                selectedIds={itemSel}
                onSelectionChange={setItemSel}
                onCellCommit={commitItemCell}
                maximizable
                exportFilename="new-invoice-item-charge"
                emptyText="No item charges."
              />
              <div className="inv-sums">
                <span>Total Qty <b>{items.reduce((n, i) => n + i.qty, 0)}</b></span>
                <span>Total Final Amount <b>{money.format(items.reduce((n, i) => n + i.finalAmount, 0))}</b></span>
              </div>
            </div>

            <div role="tabpanel" className="sd-childtabpanel inv-tabpanel" hidden={tab !== 'notes'}>
              {notes.length === 0 ? (
                <div className="inv-empty">
                  <i className="ti ti-notes" aria-hidden="true" />
                  <b>Add a note for the client or the reviewer</b>
                  It is saved with the invoice.
                  <div>
                    <button type="button" className="ca-btn" onClick={() => setNoteDraft({ title: '', text: '' })}>
                      <i className="ti ti-plus tb-i-green" aria-hidden="true" /> Add note
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="inv-tabbar">
                    <button type="button" className="ca-btn" onClick={() => setNoteDraft({ title: '', text: '' })}>
                      <i className="ti ti-plus tb-i-green" aria-hidden="true" /> Add
                    </button>
                    <button type="button" className="ca-btn" onClick={removeNotes} disabled={noteSel.size === 0}>
                      <i className="ti ti-minus tb-i-red" aria-hidden="true" /> Remove
                    </button>
                    <span className="inv-hint-r">Notes are saved with the invoice.</span>
                  </div>
                  <AppDataGrid<NoteRow>
                    rows={notes}
                    columns={noteColumns}
                    getRowId={(r) => r.oid}
                    selectable
                    selectedIds={noteSel}
                    onSelectionChange={setNoteSel}
                    exportFilename="new-invoice-notes"
                  />
                </>
              )}
            </div>
          </div>

          {/* Totals pinned above the bottom action bar. */}
          <div className="inv-sticky">
            <div className="inv-totals" aria-label="Totals">
              <span title="Analysis Charge quantities (samples × tests); item charges have their own total on the Item Charge tab">Test Qty <b>{rows.reduce((n, r) => n + r.qty, 0)}</b></span>
              <span>Detailed Amount <b>{money.format(totals?.detailedAmount ?? 0)}</b></span>
              <span className="inv-disc">Discount <b>−{money.format(totals?.discountAmount ?? 0)}</b>
                <span className="inv-muted"> ({(totals?.discount ?? 0).toFixed(2)}%)</span></span>
              <span className="inv-grand">Amount <b>{money.format(totals?.amount ?? 0)}</b></span>
            </div>
            {actionBar(true)}
          </div>
        </div>
      </div>

      <ItemChargePickerDialog
        open={pickItems}
        existingIds={existingItemIds}
        onAdd={addItemCharges}
        onClose={() => setPickItems(false)}
      />

      {/* "Default Pricing" popup (legacy TestPriceSurcharge_ListView_Invoice,
          :6027-6150): the test's price-list rows; tick one, OK re-prices this row. */}
      <AppDialog
        open={tatFor !== null}
        title="Default Pricing"
        icon="ti-clock"
        width={640}
        onClose={() => setTatFor(null)}
        footer={(
          <>
            <button type="button" className="ca-btn ca-btn-solid" onClick={acceptPricePopup}>OK</button>
            <button type="button" className="ca-btn" onClick={() => setTatFor(null)}>Cancel</button>
          </>
        )}
      >
        {tatFor && (
          <>
            <div className="inv-pricefor">
              <b>{tatFor.test}</b> · {[tatFor.matrix, tatFor.method, tatFor.component].filter(Boolean).join(' · ')}
              <span className="inv-muted"> — now {tatFor.tat ?? '—'} / {tatFor.priority ?? '—'}</span>
            </div>
            <GridSearch value={priceSearch} onChange={setPriceSearch} placeholder="Text to search..." />
            <AppDataGrid<PriceRow>
              rows={priceRows}
              columns={priceColumns}
              getRowId={(r) => r.priceId}
              selectable
              selectedIds={priceSel}
              onSelectionChange={setPriceSel}
              onRowClick={(r) => setPriceSel(new Set([r.priceId]))}
              onRowDoubleClick={(r) => { setPriceSel(new Set([r.priceId])); const c = tatFor.tatChoices.filter((x) => x.priceId === r.priceId); pickTat(tatFor, c.find((x) => x.tatId === tatFor.tatId) ?? c[0]!); }}
              getRowClassName={(r) => (r.current ? 'inv-current' : undefined)}
              defaultPageSize={20}
              hideHeaderTools
              emptyText="No default price for this test."
            />
          </>
        )}
      </AppDialog>

      {/* Parameter popup — the analytes behind the row (legacy :5969-6025), view only. */}
      <AppDialog
        open={paramsFor !== null}
        title="Parameters"
        icon="ti-list-details"
        width={460}
        onClose={() => setParamsFor(null)}
        footer={<button type="button" className="ca-btn" onClick={() => setParamsFor(null)}>Close</button>}
      >
        {paramsFor && (
          <>
            <div className="inv-pricefor">
              <b>{paramsFor.test}</b> · {[paramsFor.matrix, paramsFor.method, paramsFor.component].filter(Boolean).join(' · ')}
            </div>
            <AppDataGrid<{ name: string }>
              rows={paramsFor.parameters.map((name) => ({ name }))}
              columns={[{ field: 'name', header: 'Parameter' }]}
              getRowId={(p) => p.name}
              defaultPageSize={10}
              exportFilename="invoice-row-parameters"
            />
          </>
        )}
      </AppDialog>

      {cellMenu && (
        <>
          <div className="inv-menu-backdrop" onClick={() => setCellMenu(null)}
               onContextMenu={(e) => { e.preventDefault(); setCellMenu(null); }} />
          <div className="inv-menu" role="menu" style={{ left: cellMenu.x, top: cellMenu.y }}
               onKeyDown={(e) => { if (e.key === 'Escape') setCellMenu(null); }}>
            <button type="button" role="menuitem" autoFocus onClick={copyToAll}>
              <i className="ti ti-copy" aria-hidden="true" /> Copy To All Cell
            </button>
          </div>
        </>
      )}

      <AppDialog
        open={noteDraft !== null}
        title="Add Note"
        icon="ti-notes"
        width={520}
        onClose={() => setNoteDraft(null)}
        footer={(
          <>
            <button type="button" className="ca-btn ca-btn-solid" onClick={addNote}>OK</button>
            <button type="button" className="ca-btn" onClick={() => setNoteDraft(null)}>Cancel</button>
          </>
        )}
      >
        <div className="inv-notegrid">
          <label htmlFor="inv-note-title">Title</label>
          <input id="inv-note-title" type="text" className="sd-input" maxLength={100}
                 value={noteDraft?.title ?? ''}
                 onChange={(e) => setNoteDraft((d) => (d ? { ...d, title: e.target.value } : d))} />
          <label htmlFor="inv-note-text">Note<span className="req">*</span></label>
          <textarea id="inv-note-text" rows={5} className={`sd-input${noteDraft?.text.trim() ? '' : ' required-empty'}`}
                    value={noteDraft?.text ?? ''}
                    onChange={(e) => setNoteDraft((d) => (d ? { ...d, text: e.target.value } : d))} />
        </div>
      </AppDialog>

      <AppDialog
        open={priceFor !== null}
        title="Set default price"
        icon="ti-tag"
        width={460}
        onClose={() => setPriceFor(null)}
        footer={(
          <>
            <button type="button" className="ca-btn ca-btn-solid" onClick={() => void saveSetPrice()}>Save price</button>
            <button type="button" className="ca-btn" onClick={() => setPriceFor(null)}>Cancel</button>
          </>
        )}
      >
        {priceFor && (
          <div className="inv-pricegrid">
            <div className="inv-pricefor">
              <b>{priceFor.row.test}</b> · {priceFor.row.method ?? ''} · {priceFor.row.matrix ?? ''} · {priceFor.row.component ?? ''}
            </div>
            <label htmlFor="inv-price-pri">Priority</label>
            <select id="inv-price-pri" className="sd-input" value={priceFor.priority}
                    onChange={(e) => setPriceFor({ ...priceFor, priority: e.target.value })}>
              {lookups.priorities.map((p) => <option key={p.oid} value={p.oid}>{p.name ?? ''}</option>)}
            </select>
            <label htmlFor="inv-price-tat">TAT</label>
            <select id="inv-price-tat" className="sd-input" value={priceFor.tat}
                    onChange={(e) => setPriceFor({ ...priceFor, tat: e.target.value })}>
              {lookups.tats.map((t) => <option key={t.oid} value={t.name ?? ''}>{t.name ?? ''}</option>)}
            </select>
            <label htmlFor="inv-price-val">Price<span className="req">*</span></label>
            <input id="inv-price-val" type="text" inputMode="decimal" className="sd-input inv-money"
                   value={priceFor.price} placeholder="0.00"
                   onChange={(e) => setPriceFor({ ...priceFor, price: e.target.value })} />
            <div className="inv-muted inv-pricenote">
              <i className="ti ti-info-circle" aria-hidden="true" /> Saved to Settings › Test Pricing › Default Pricing,
              then this invoice is re-priced.
            </div>
          </div>
        )}
      </AppDialog>
    </div>
  );
}

/** A wire date → the `yyyy-MM-dd` an <input type="date"> needs, through the
 *  shared helper so no date formatting is inlined here (C2). */
function dateInput(v: string | null): string {
  if (!v) return '';
  const [m, d, y] = fmtLocalDate(v).split('/');
  return y && m && d ? `${y}-${m}-${d}` : '';
}

function today(): string {
  return dateInput(new Date().toISOString());
}
